import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  TextField,
  Checkbox,
  CircularProgress,
  Chip,
  Box,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppContext from "../../AppContext";
import {
  findUserByEmail,        // ← bunu kaldırabilirsin artık, kullanmayacağız
  createGroupReceipt,
  createReceiptForUser,
  createReceipt,
  getPaymentUrl,
  updateReceipt,
  updateGroupReceipt,
  getUserPaidProducts, 
  checkStockAvailability   // ← bunu ekle
} from "../../db/firebaseMethods";
import { useNavigate, Navigate } from "react-router-dom";
import moment from "moment/moment";
import "moment/locale/tr";
import "./bulkPaymentPage.css";

// ─────────────────────────────────────────────────────────────────────────────
// Yardımcı: belirli bir üye için çakışan ürünleri hesapla
// seçili + ödenmiş ürünler birlikte değerlendiriliyor
// ─────────────────────────────────────────────────────────────────────────────
function computeOverlaps(memberSelectedProducts, memberPaidProducts, allProducts) {
  const activeIds = Object.keys({ ...memberSelectedProducts, ...memberPaidProducts });
  const overlapping = new Set();

  Object.values(allProducts).forEach((w) => {
    if (!w.id || w.product_type === 1 || w.product_type === 3) return;
    if (activeIds.includes(w.id)) return; // zaten seçili, kendisiyle çakışma yok

    let isOverlap = false;
    activeIds.forEach((pId) => {
      const other = allProducts[pId];
      if (!other || other.id === w.id || other.product_type === 3 || other.product_type === 1) return;
      const maxStart = Math.max(w.start_at, other.start_at);
      const minEnd   = Math.min(w.end_at,   other.end_at);
      if (maxStart < minEnd) isOverlap = true;
    });

    if (isOverlap) overlapping.add(w.id);
  });

  return overlapping; // Set<productId>
}

// ─────────────────────────────────────────────────────────────────────────────
export default function BulkPaymentPage() {
  const { user, activeSeason } = useContext(AppContext);
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [paidProducts,  setPaidProducts]  = useState({});
  const [mainProduct,   setMainProduct]   = useState(null);
  const [workshopDays,  setWorkshopDays]  = useState({}); // { "26 Ekim...": [workshop, ...] }

  const [searchEmail,   setSearchEmail]   = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult,  setSearchResult]  = useState(null);
  const [searchError,   setSearchError]   = useState("");

  const [members,    setMembers]    = useState([]);
  const [payLoading, setPayLoading] = useState(false);

  const activeSeasonProducts = activeSeason?.products || {};

  // ── Ödenen ürünleri hesapla ─────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.activeSeasonReceipts) return;
    const paid = {};
    user.activeSeasonReceipts.forEach((r) => {
      Object.keys(r.receipt_products || {}).forEach((pId) => { paid[pId] = true; });
    });
    setPaidProducts(paid);
  }, [user]);

  // ── activeSeason'dan mainProduct + workshopDays çıkar ──────────────────────
  useEffect(() => {
    setMainProduct(null);
    setWorkshopDays({});
    if (!activeSeason?.products) return;
    moment.locale("tr");
    let main = null;
    let days = {};

    Object.keys(activeSeason.products).forEach((pId) => {
      const p = { ...activeSeason.products[pId], id: pId };
      if (p.product_type === 1) {
        main = p;
      } else if (p.start_at && p.product_type !== 1) {
        const dayStr = moment(p.start_at).format("DD MMMM YYYY dddd");
        if (!days[dayStr]) days[dayStr] = [];
        days[dayStr].push(p);
        days[dayStr].sort((a, b) => a.start_at - b.start_at);
      }
    });

    setMainProduct(main);
    setWorkshopDays(days);
  }, [activeSeason]);

  // ── members'ı mainProduct hazır olunca başlat ──────────────────────────────
  useEffect(() => {
    if (!mainProduct || !user) return;
    if (members.length > 0) return;
    const selected = {};
    if (!paidProducts[mainProduct.id]) selected[mainProduct.id] = true;
    setMembers([{
      uid: user.uid, name: user.name, email: user.email,
      selectedProducts: selected, isSelf: true,
    }]);
  }, [mainProduct, user, paidProducts]);

  // ── Toplam fiyat ───────────────────────────────────────────────────────────
  const calculateTotal = () => {
    let total = 0;
    members.forEach((member) => {
      Object.keys(member.selectedProducts).forEach((pId) => {
        const alreadyPaid = member.isSelf && paidProducts[pId];
        if (!alreadyPaid) total += activeSeasonProducts[pId]?.price || 0;
      });
    });
    return total;
  };

  // ── Kişi arama ─────────────────────────────────────────────────────────────
  const doSearch = async (email) => {
    setSearchLoading(true);
    setSearchResult(null);
    setSearchError("");
    try {
      const { paidProducts: foundPaidProducts, uid, name, email: foundEmail } = await getUserPaidProducts(
        email.trim().toLowerCase(),
        activeSeason?.id
      );
      if (!uid) { setSearchError("Bu e-posta adresiyle kayıtlı kullanıcı bulunamadı."); return; }
      if (uid === user.uid) { setSearchError("Kendinizi tekrar ekleyemezsiniz."); return; }
      if (members.find((m) => m.uid === uid)) { setSearchError("Bu kişi zaten listeye eklenmiş."); return; }
      setSearchResult({ uid, name, email: foundEmail, paidProducts: foundPaidProducts });
    } catch (err) {
      setSearchError("Arama sırasında bir hata oluştu: " + err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchEmail.includes("@") || !searchEmail.trim()) return;
    doSearch(searchEmail);
  };

  // "@" içeren geçerli görünümlü email yazıldığında otomatik ara (700ms debounce)
  useEffect(() => {
    const trimmed = searchEmail.trim();
    if (!trimmed.includes("@") || trimmed.length < 6) return;
    const timer = setTimeout(() => { doSearch(trimmed); }, 700);
    return () => clearTimeout(timer);
  }, [searchEmail]); // eslint-disable-line react-hooks/exhaustive-deps

 /* const handleAddMember = async (found) => {
  const selected = {};
  if (mainProduct) selected[mainProduct.id] = true;

  // O kişinin önceki ödemelerini çek
  let memberPaid = {};
  try {

    const receipts = await pullReceiptsByUser(found.id || found.uid, activeSeason?.id);
console.log("uid:", found.id || found.uid);
console.log("seasonId:", activeSeason?.id);
console.log("receipts:", receipts);
    receipts.forEach((r) => {
      Object.keys(r.receipt_products || {}).forEach((pId) => {
        memberPaid[pId] = true;
      });
    });
  } catch (err) {
    console.error("Üye ödemeleri çekilemedi:", err);
  }

  // Zaten ödediği ürünleri seçiliden çıkar
  Object.keys(memberPaid).forEach((pId) => {
    delete selected[pId];
  });

  setMembers((prev) => [
    ...prev,
    {
      uid: found.id || found.uid,
      name: found.name || found.email,
      email: found.email,
      selectedProducts: selected,
      paidProducts: memberPaid,  // ← üyeye özel paidProducts
      isSelf: false,
    },
  ]);
  setSearchResult(null);
  setSearchEmail("");
  setSearchError("");
};*/
const handleAddMember = (found) => {
  if (members.find((m) => m.uid === found.uid)) {
    setSearchError("Bu kişi zaten listeye eklenmiş.");
    setSearchResult(null);
    setSearchEmail("");
    return;
  }

  const selected = {};
  if (mainProduct && !found.paidProducts?.[mainProduct.id]) {
    selected[mainProduct.id] = true;
  }

  setMembers((prev) => [...prev, {
  uid: found.uid,
  name: found.name || found.email || found.uid,  // ← fallback ekle
  email: found.email || "",
  selectedProducts: selected,
  paidProducts: found.paidProducts || {},
  isSelf: false,
}]);
  setSearchResult(null);
  setSearchEmail("");
  setSearchError("");
};

  const handleRemoveMember = (uid) => setMembers((prev) => prev.filter((m) => m.uid !== uid));

  const handleToggleProduct = (memberUid, productId) => {
    if (productId === mainProduct?.id) {
      alert("Kongre katılım bedeli zorunludur ve sepetten çıkarılamaz.");
      return;
    }
    setMembers((prev) =>
      prev.map((m) => {
        if (m.uid !== memberUid) return m;
        const next = { ...m.selectedProducts };
        if (next[productId]) delete next[productId];
        else next[productId] = true;
        return { ...m, selectedProducts: next };
      })
    );
  };

  // ── Ödeme başlat ───────────────────────────────────────────────────────────
const handleProceedToPayment = async () => {
  if (!user.name || !user.phone) {
    alert("Ödeme adımına ilerlemeden önce lütfen profil bilgilerinizi (ad-soyad, telefon) eksiksiz doldurunuz.");
    return;
  }

  const totalPrice = calculateTotal();
  setPayLoading(true);

  // ── Kontrol edilecek ürünleri say ────────────────────────────────────────
  const productCounts = {};
  members.forEach((member) => {
    Object.keys(member.selectedProducts).forEach((pId) => {
      const alreadyPaid = member.isSelf && paidProducts[pId];
      if (alreadyPaid) return;
      const p = activeSeasonProducts[pId];
      if (!p || p.product_type === 1 || p.product_type === 3) return;
      productCounts[pId] = (productCounts[pId] || 0) + 1;
    });
  });

  try {
    // ── 1. STOK VAR MI KONTROLÜ (read-only) ───────────────────────────────
    if (Object.keys(productCounts).length > 0) {
      const insufficient = await checkStockAvailability(activeSeason.id, productCounts);

      if (insufficient.length > 0) {
        const insufficientIds = insufficient.map((d) => d.id);
        const names = insufficient
          .map((d) => `${d.title} (kalan: ${d.available}, istenen: ${d.requested})`)
          .join("\n");

        alert(
          "Üzgünüz, aşağıdaki atölyeler için yeterli yer kalmadı:\n" +
          names +
          "\n\nBu atölyeler seçimlerinizden kaldırıldı. Lütfen kontrol edip tekrar deneyin."
        );

        setMembers((prev) =>
          prev.map((m) => {
            const next = { ...m.selectedProducts };
            insufficientIds.forEach((pId) => delete next[pId]);
            return { ...m, selectedProducts: next };
          })
        );

        setPayLoading(false);
        return; // ← ödeme ekranına gidilmez
      }
    }

    // ── 2. Group receipt oluştur ──────────────────────────────────────────
    const groupReceiptId = await createGroupReceipt({
      payer_id: user.uid,
      payer_email: user.email,
      season_id: activeSeason?.id,
      total_price: totalPrice,
      is_paid: false,
      members: [],
    });

    // ── 3. Her üye için receipt oluştur ───────────────────────────────────
    const memberReceipts = [];
    for (const member of members) {
      const productsToCreate = {};
      Object.keys(member.selectedProducts).forEach((pId) => {
        const alreadyPaid = member.isSelf && paidProducts[pId];
        if (!alreadyPaid) productsToCreate[pId] = true;
      });
      if (Object.keys(productsToCreate).length === 0) continue;

      const receiptId = await createReceiptForUser({
        season_id: activeSeason?.id,
        is_paid: false,
        receipt_products: productsToCreate,
        email: member.email,
        user_id: member.uid,
        paid_by_group: member.isSelf ? false : true,
        paid_by_name: member.isSelf ? null : user.name,
        paid_by_email: member.isSelf ? null : user.email,
        group_receipt_id: groupReceiptId,
        price: Object.keys(productsToCreate).reduce(
          (acc, pId) => acc + (activeSeasonProducts[pId]?.price || 0),
          0
        ),
      });
      memberReceipts.push({ user_id: member.uid, receipt_id: receiptId, products: productsToCreate });
    }

    if (memberReceipts.length === 0) {
      alert("Ödenecek ürün bulunamadı.");
      setPayLoading(false);
      return;
    }

    // ── 4. Group receipt'i güncelle ───────────────────────────────────────
    await updateGroupReceipt(groupReceiptId, { members: memberReceipts });

    // ── 5. Sıfır tutarsa direkt sonuç sayfasına git ───────────────────────
    if (totalPrice === 0) {
      navigate("/payment_result?zero_payment=true&group_receipt_id=" + groupReceiptId);
      return;
    }

    // ── 6. Ödeme URL'si al ─────────────────────────────────────────────────
    const redirectResponse = await getPaymentUrl({
      name: user.name,
      email: user.email,
      firebase_receipt_id: groupReceiptId,
      requester_url: window.location.origin,
      price: totalPrice,
    });

    if (redirectResponse?.url) {
      const url = redirectResponse.url;
      const token = url.split("/").pop();
      await updateGroupReceipt(groupReceiptId, { token });
      window.location.href = url + "?group_receipt_id=" + groupReceiptId;
    } else {
      alert("Ödeme ekranı açılırken bir sorun oluştu. (Hata Kodu: GRUP01)");
    }
  } catch (err) {
    console.error("Toplu ödeme hatası:", err);
    alert("İşlem sırasında bir hata oluştu: " + err.message);
  } finally {
    setPayLoading(false);
  }
};

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (!user) return <Navigate to="/login" />;

  if (!activeSeason) {
    return (
      <div className="bulkPage__loading">
        <p className="bulkPage__inactiveMsg">
          Kongre ve atölye başvuruları için sistem yakında aktif hale gelecektir.
          Detaylı bilgi için{" "}
          <a href="mailto:info@tzv.org.tr" className="bulkPage__inactiveLink">info@tzv.org.tr</a>{" "}
          adresine e-posta gönderebilirsiniz.
        </p>
      </div>
    );
  }

  if (!mainProduct || members.length === 0) {
    return (
      <div className="bulkPage__loading">
        <CircularProgress color="secondary" />
        <p>Yükleniyor...</p>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    
    <div className="bulkPage__container">
      <div className="bulkPage__header">
        <button className="bulkPage__backBtn" onClick={() => navigate("/profile")}>
          <ArrowBackIcon className="bulkPage__backIcon" /> Profile Dön
        </button>
        <h1 className="bulkPage__title">
          <PeopleIcon className="bulkPage__titleIcon" />
          Toplu Ödeme
        </h1>
        <Alert severity="info" sx={{ mt: 2, textAlign: "left", fontSize: "0.85rem" }}>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
            <li>Ödeme işlemi sırasında katılımcının kongreye kayıtlı e-posta adresinin eksiksiz ve doğru girilmesi gerekmektedir.</li>
            <li>Toplu ödeme yapılacak kişilerin sistemde kayıtlı olması gerekmektedir.</li>
            <li>Kurumsal ödeme taleplerinde, kurumun bilgisi ve onayı dahilinde talepte bulunulması gerekmektedir.</li>
          </ul>
        </Alert>
      </div>

      <div className="bulkPage__body">
        <div className="bulkPage__left">

          {/* Kişi arama */}
          <div className="bulkPage__card">
            <h2 className="bulkPage__cardTitle">Kişi Ekle</h2>
            <p className="bulkPage__cardHint">Sisteme kayıtlı kişinin e-posta adresini giriniz</p>
            <Box sx={{ mb: 0.5 }}>
              <Box
                component="span"
                sx={{
                  display: "block",
                  color: "#302b63",
                  fontSize: "0.76rem",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  textTransform: "uppercase",
                }}
              >
                E-Posta Adresi
              </Box>
              <div className="bulkPage__searchRow">
                <TextField
                  size="small"
                  fullWidth
                  value={searchEmail}
                  onChange={(e) => { setSearchEmail(e.target.value); setSearchError(""); setSearchResult(null); }}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="ornek@email.com"
                  helperText={searchLoading ? "Aranıyor..." : " "}
                  FormHelperTextProps={{ sx: { color: searchLoading ? "#7b6be0" : "#d32f2f", mt: 0.5 } }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSearch}
                  disabled={searchLoading || !searchEmail.includes("@")}
                  startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  className="bulkPage__searchBtn"
                  sx={{ mt: "1px", flexShrink: 0 }}
                >
                  Ara
                </Button>
              </div>
            </Box>
            {searchError && <p className="bulkPage__searchError">{searchError}</p>}
            {searchResult && (
              <div className="bulkPage__foundCard">
                <div className="bulkPage__avatar bulkPage__avatar--other">
                  {(searchResult.name || searchResult.email || "?").charAt(0).toUpperCase()}
                </div>
                <div className="bulkPage__foundInfo">
                  <div className="bulkPage__foundName">{searchResult.name || "—"}</div>
                  <div className="bulkPage__foundEmail">{searchResult.email || "—"}</div>
                </div>
                <Button variant="contained" color="secondary" size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => handleAddMember(searchResult)}
                  className="bulkPage__addBtn"
                >
                  Ekle
                </Button>
              </div>
            )}
          </div>

          {/* Üye kartları */}
          {members.map((member) => (
            <MemberCard
              key={member.uid}
              member={member}
              mainProduct={mainProduct}
              workshopDays={workshopDays}
              activeSeasonProducts={activeSeasonProducts}
              paidProducts={paidProducts}
              onToggleProduct={handleToggleProduct}
              onRemove={handleRemoveMember}
            />
          ))}
        </div>

        {/* Sipariş özeti */}
        <div className="bulkPage__right">
          <div className="bulkPage__summaryCard">
            <h2 className="bulkPage__cardTitle">Sipariş Özeti</h2>

            {members.map((member) => {
              const memberTotal = Object.keys(member.selectedProducts).reduce((acc, pId) => {
                const alreadyPaid = member.isSelf && paidProducts[pId];
                return alreadyPaid ? acc : acc + (activeSeasonProducts[pId]?.price || 0);
              }, 0);
              if (memberTotal === 0) return null;
              return (
                <div key={member.uid} className="bulkPage__summaryMember">
                  <div className="bulkPage__summaryMemberName">
                    {member.name || member.email || "?"}
                    {member.isSelf && <span className="bulkPage__selfBadge">Ben</span>}
                  </div>
                  {Object.keys(member.selectedProducts).map((pId) => {
                    const alreadyPaid = member.isSelf && paidProducts[pId];
                    if (alreadyPaid) return null;
                    const p = activeSeasonProducts[pId];
                    return (
                      <div key={pId} className="bulkPage__summaryRow">
                        <span className="bulkPage__summaryLabel">{p?.title}</span>
                        <span className="bulkPage__summaryPrice">{p?.price} TL</span>
                      </div>
                    );
                  })}
                  <div className="bulkPage__summaryMemberTotal">
                    <span>Ara toplam</span>
                    <span>{memberTotal.toLocaleString("tr-TR")} TL</span>
                  </div>
                </div>
              );
            })}

            <div className="bulkPage__totalBar">
              <span>Genel Toplam</span>
              <span className="bulkPage__totalAmount">{calculateTotal().toLocaleString("tr-TR")} TL</span>
            </div>

            <Button 
                fullWidth 
                variant="contained"
                onClick={handleProceedToPayment}
                disabled={payLoading || calculateTotal() === 0}
                className="bulkPage__payBtn"
              >
                {payLoading ? "İşleniyor..." : "Ödemeye İlerle"}
              </Button>

            <p className="bulkPage__payNote">
              Ödeme tamamlandığında her kişinin profilinde "Ödendi" olarak görünecektir.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MemberCard — gün grupları, stok filtresi, saat gösterimi, çakışma kontrolü
// ─────────────────────────────────────────────────────────────────────────────
function MemberCard({ member, mainProduct, workshopDays, activeSeasonProducts, paidProducts, onToggleProduct, onRemove }) {
  const { isSelf } = member;

  // Bu üye için ödenmiş ürünler (kendisiyse receipts'ten, değilse boş)
  const memberPaid = isSelf ? paidProducts : (member.paidProducts || {});


  // Çakışan ürünleri hesapla
  const overlapping = computeOverlaps(member.selectedProducts, memberPaid, activeSeasonProducts);

  const paymentOkIndicator = (
    <Chip variant="outlined" icon={<CheckCircleIcon />} label="ÖDENDİ" color="success" size="small" className="bulkPage__paidChip" />
  );

  return (
    <div className={`bulkPage__memberCard ${isSelf ? "bulkPage__memberCard--self" : ""}`}>
      {/* Üye başlığı */}
      <div className="bulkPage__memberHeader">
        <div className={`bulkPage__avatar ${isSelf ? "bulkPage__avatar--self" : "bulkPage__avatar--other"}`}>
          {(member.name || member.email).charAt(0).toUpperCase()}
        </div>
        <div className="bulkPage__memberMeta">
          <span className="bulkPage__memberName">
            {member.name || "—"}
            {isSelf && <span className="bulkPage__selfBadge">Ben</span>}
          </span>
          <div className="bulkPage__memberEmail">{member.email}</div>
        </div>
        {!isSelf && (
          <Button size="small" color="error" variant="outlined"
            onClick={() => onRemove(member.uid)}
            className="bulkPage__removeBtn"
          >
            Çıkar
          </Button>
        )}
      </div>

      {/* Kongre — zorunlu */}
      {mainProduct && (() => {
        const alreadyPaid = memberPaid[mainProduct.id]; // ← isSelf && kaldır
        return (
          <div className="bulkPage__productRow bulkPage__productRow--selected bulkPage__productRow--disabled">
            {alreadyPaid ? paymentOkIndicator : (
              <Checkbox size="small" checked disabled color="secondary" className="bulkPage__checkbox" />
            )}
            <span className="bulkPage__productLabel">{mainProduct.title}</span>
            <span className="bulkPage__productMandatory">zorunlu</span>
            {!alreadyPaid && <span className="bulkPage__productPrice">{activeSeasonProducts[mainProduct.id]?.price ?? mainProduct.price} TL</span>}
          </div>
        );
      })()}

      {/* Atölyeler — gün grupları */}
      {Object.keys(workshopDays)
        .sort((a, b) => a.localeCompare(b))
        .map((day) => {
          // Sadece bu günde gösterilecek atölyeleri filtrele:
          // stok > 0 VEYA bu üye için ödenmiş
          const workshops = workshopDays[day].filter((w) => {
            const alreadyPaid = memberPaid[w.id];
            return !alreadyPaid && (w.stock ?? 0) > 0; // ← ödenmiş olanları gizle, sadece stokta olanları göster
          });
          if (workshops.length === 0) return null;

          return (
            <div key={day} className="bulkPage__dayGroup">
              <div className="bulkPage__dayHeader">{day}</div>
              {workshops.map((w) => {
                const alreadyPaid = memberPaid[w.id];
                const isSelected    = !!member.selectedProducts[w.id];
                const isOverlapping = overlapping.has(w.id) && !alreadyPaid;
                const timeStr       = moment(w.start_at).format("HH:mm") + " – " + moment(w.end_at).format("HH:mm");

                const rowCls = [
                  "bulkPage__productRow",
                  alreadyPaid                    ? "bulkPage__productRow--paid"     : "",
                  !alreadyPaid && isSelected     ? "bulkPage__productRow--selected" : "",
                  isOverlapping                  ? "bulkPage__productRow--overlap"  : "",
                  !alreadyPaid && !isOverlapping ? "bulkPage__productRow--clickable": "",
                ].filter(Boolean).join(" ");

                const handleClick = () => {
                  if (alreadyPaid) return;
                  if (isOverlapping) {
                    alert("Seçtiğiniz başka bir atölyenin saatleri ile çakıştığı için bu atölyeyi seçemezsiniz.");
                    return;
                  }
                  onToggleProduct(member.uid, w.id);
                };

                return (
                  <div key={w.id} className={rowCls} onClick={handleClick}
                    title={isOverlapping ? "Saat çakışması var" : ""}>
                    {alreadyPaid ? paymentOkIndicator : (
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        disabled={isOverlapping}
                        color="secondary"
                        className="bulkPage__checkbox"
                      />
                    )}
                    <div className="bulkPage__productInfo">
                      <span className="bulkPage__productLabel">{w.title}</span>
                      <span className="bulkPage__productTime">{timeStr}</span>
                    </div>
                    {isOverlapping && <span className="bulkPage__overlapBadge">Çakışıyor</span>}
                    {!alreadyPaid && <span className="bulkPage__productPrice">{w.price} TL</span>}
                  </div>
                );
              })}
            </div>
          );
        })}
    </div>
  );
}